package com.evoapps.evoshift.domain.model

/**
 * SH-269: the sections of a workplace's settings that can follow its workplace group (SH-267),
 * each as a whole: a workplace either follows the group in a section or keeps its own.
 *
 * The ONE list of sections: the resolver ([GroupSettingsResolver]), "Reset to group", smart sync,
 * the settings screens and the bulk changes use it, and the functions mirror it in
 * functions/groupSettingsSections.json ([id], [storage], [paths], [finances] agree:
 * GroupSettingsSectionsAgreementTest). [paths] are the stored field names (workplaces/{id}.settings
 * or workplaces/{id}/financials/data); always the workplace's own: its name, address and location
 * (and the voivodeship read from it), who works without pay, the labour budget.
 */
enum class SettingsSection(
    /** Stored in workplace_groups/{id} and group_settings/current.inheritedSections. */
    val id: String,
    val storage: Storage,
    val paths: List<String>,
    /** Only owners and financial managers switch it (firestore.rules guard those fields). */
    val finances: Boolean = false
) {
    INCOME("income", Storage.FINANCIALS, listOf("commissionPercent", "fixedRevenueMonthly", "includeFixedRevenueInDailyFinancialReport"), finances = true),
    SALES_TARGETS("salesTargets", Storage.FINANCIALS, listOf("salesTargets"), finances = true),
    LABOUR_TARGETS("labourTargets", Storage.FINANCIALS, listOf("labourTargetPercent", "labourTargetBase", "salesPerLabourHourGoal"), finances = true),
    REGION("region", Storage.SETTINGS, listOf("timezone", "currency", "firstDayOfWeek")),
    SWAP_POLICY("swapPolicy", Storage.SETTINGS, listOf("shiftSwapPolicy")),
    ROLES_SKILLS("rolesSkills", Storage.SETTINGS, listOf("availableRoles", "workplaceSkills", "leadRoles", "leadSkills")),
    AVAILABILITY_SLOTS("availabilitySlots", Storage.SETTINGS, listOf("definedAvailabilitySlots")),
    /** The shift and day templates with the weekly skeleton: the skeleton refers to the templates' ids. */
    TEMPLATES("templates", Storage.SETTINGS, listOf("defaultWeeklySkeleton")),
    SUNDAYS("sundays", Storage.SETTINGS, listOf("sundayPolicy")),
    REQUESTS("requests", Storage.SETTINGS, listOf("requests")),
    REST_RULES("restRules", Storage.SETTINGS, listOf("restRulesForAllContracts")),
    STAFF_CHECKS("staffChecks", Storage.SETTINGS, listOf("blockExpiredStaffChecks"), finances = true),
    PAYROLL(
        "payroll", Storage.SETTINGS,
        listOf(
            "market", "payroll.accidentInsuranceRate", "payroll.nightStart", "payroll.nightEnd", "payroll.ownerShiftsUnpaid",
            "payroll.settlementPeriodMonths", "payroll.overtimeTimeOffInLieu", "payroll.openShiftCostMode", "payroll.openShiftCostPerHour"
        ),
        finances = true
    ),
    BREAKS("breaks", Storage.SETTINGS, listOf("breakPolicy"), finances = true),
    CLOCK_REVIEW("clockReview", Storage.SETTINGS, listOf("clockReview"), finances = true),
    GEOFENCE("geofence", Storage.SETTINGS, listOf("geofence")),
    ALERTS("alerts", Storage.SETTINGS, listOf("noShowAlertMinutes", "checklistAlertMinutes")),
    WORK_HOURS("workHours", Storage.SETTINGS, listOf("workHours")),
    REPORTS("reports", Storage.SETTINGS, listOf("reports")),
    ROSTER_VISIBILITY("rosterVisibility", Storage.SETTINGS, listOf("rosterVisibility")),
    WEATHER("weather", Storage.SETTINGS, listOf("weatherEnabled")),
    SPECIAL_DAYS("specialDays", Storage.SETTINGS, listOf("specialDaysEnabled")),
    FOOTER("footer", Storage.SETTINGS, listOf("showEvoShiftFooter"));

    enum class Storage { SETTINGS, FINANCIALS }

    /** The section also holds the shift and day template sub-collections. */
    val hasTemplates: Boolean get() = this == TEMPLATES

    companion object {
        fun fromId(id: String): SettingsSection? = entries.firstOrNull { it.id == id }
    }
}

/** A workplace's (or a group's) values of every section; [financials] null when not readable. */
data class SettingsValues(
    val settings: WorkplaceSettings,
    val financials: WorkplaceFinancials? = null
)

/**
 * SH-269: a workplace's link to its group (workplaces/{id}/group_settings/current, written by the
 * server only): the group's values for its managers and the sections that follow the group.
 */
data class GroupSettingsLink(
    val groupId: String,
    /** Blank for the default group without a name of its own ("All workplaces"). */
    val groupName: String,
    val isDefault: Boolean,
    val workplaceCount: Int,
    val group: SettingsValues?,
    val inherited: Set<SettingsSection>
) {
    val usesDefaultName: Boolean get() = isDefault && groupName.isBlank()

    /** Several workplaces share the group: the screens show "Group settings" / "Own settings". */
    val isShared: Boolean get() = workplaceCount > 1

    fun follows(section: SettingsSection): Boolean = section in inherited
}

/** SH-269: whether a workplace follows its group in a section, or keeps its own. */
enum class SectionSource { GROUP, OWN }

/**
 * SH-269: the group's values of a group (workplace_groups/{id}.settings, .financials) as its owner
 * edits them, with who follows each section.
 */
data class GroupSettings(
    val groupId: String,
    val values: SettingsValues?,
    /** Per section, the workplaces that follow the group and those that keep their own. */
    val following: Map<SettingsSection, List<String>> = emptyMap(),
    val own: Map<SettingsSection, List<String>> = emptyMap()
)

/** SH-269: what a group save reached, per section: "Applies to N workplaces; X keeps its own". */
data class GroupSaveSummary(
    val following: Map<SettingsSection, List<String>>,
    val own: Map<SettingsSection, List<String>>
) {
    /** The workplaces the save changes (those following any of the sections saved). */
    val appliesTo: Set<String> get() = following.values.flatten().toSet()
    /** The workplaces that keep their own in at least one saved section. */
    val keepOwn: Set<String> get() = own.values.flatten().toSet()
}

/**
 * SH-269: "workplace's own, else the group's" by section, the app's twin of
 * functions/groupSettings.js (resolve, sameSection). Each section is read and written as a whole
 * through its fields of [WorkplaceSettings] / [WorkplaceFinancials]; the exhaustive `when`s keep
 * every section covered.
 */
object GroupSettingsResolver {
    /** [section]'s values in [values], comparable with ==. Templates: the weekly skeleton (the sets compare apart). */
    fun project(section: SettingsSection, values: SettingsValues): Any? {
        val s = values.settings
        val f = values.financials
        return when (section) {
            SettingsSection.INCOME -> f?.let { listOf(it.commissionPercent, it.fixedRevenueMonthly, it.includeFixedRevenueInDailyFinancialReport) }
            SettingsSection.SALES_TARGETS -> f?.salesTargets
            SettingsSection.LABOUR_TARGETS -> f?.let { listOf(it.labourTargetPercent, it.salesPerLabourHourGoal) }
            SettingsSection.REGION -> listOf(s.timezone, s.currency, s.firstDayOfWeek)
            SettingsSection.SWAP_POLICY -> s.shiftSwapPolicy
            SettingsSection.ROLES_SKILLS -> listOf(s.availableRoles, s.workplaceSkills, s.leadRoles.toSet(), s.leadSkills.toSet())
            SettingsSection.AVAILABILITY_SLOTS -> s.definedAvailabilitySlots
            SettingsSection.TEMPLATES -> s.defaultWeeklySkeleton
            SettingsSection.SUNDAYS -> s.sundayPolicy
            SettingsSection.REQUESTS -> s.requests
            SettingsSection.REST_RULES -> s.restRulesForAllContracts
            SettingsSection.STAFF_CHECKS -> s.blockExpiredStaffChecks
            SettingsSection.PAYROLL -> listOf(s.market, s.payroll.copy(unpaidUserIds = emptyList(), unpaidPeriods = emptyMap()))
            SettingsSection.BREAKS -> s.breakPolicy
            SettingsSection.CLOCK_REVIEW -> s.clockReview
            SettingsSection.GEOFENCE -> s.geofence
            SettingsSection.ALERTS -> listOf(s.noShowAlertMinutes, s.checklistAlertMinutes)
            SettingsSection.WORK_HOURS -> s.workHours
            SettingsSection.REPORTS -> s.reports
            SettingsSection.ROSTER_VISIBILITY -> s.rosterVisibility
            SettingsSection.WEATHER -> s.weatherEnabled
            SettingsSection.SPECIAL_DAYS -> s.specialDaysEnabled
            SettingsSection.FOOTER -> s.showEvoShiftFooter
        }
    }

    /** Whether [a] and [b] hold the same values of [section]. */
    fun same(section: SettingsSection, a: SettingsValues, b: SettingsValues): Boolean = project(section, a) == project(section, b)

    /** The sections whose values differ between [before] and [after]. */
    fun changed(before: SettingsValues, after: SettingsValues): Set<SettingsSection> =
        SettingsSection.entries.filterTo(mutableSetOf()) { !same(it, before, after) }

    /** [into] with [section]'s values taken from [from] (the workplace-only fields stay [into]'s). */
    fun copy(section: SettingsSection, from: SettingsValues, into: SettingsValues): SettingsValues {
        val s = into.settings
        val g = from.settings
        val settings = when (section) {
            SettingsSection.REGION -> s.copy(timezone = g.timezone, currency = g.currency, firstDayOfWeek = g.firstDayOfWeek)
            SettingsSection.SWAP_POLICY -> s.copy(shiftSwapPolicy = g.shiftSwapPolicy)
            SettingsSection.ROLES_SKILLS -> s.copy(availableRoles = g.availableRoles, workplaceSkills = g.workplaceSkills, leadRoles = g.leadRoles, leadSkills = g.leadSkills)
            SettingsSection.AVAILABILITY_SLOTS -> s.copy(definedAvailabilitySlots = g.definedAvailabilitySlots)
            SettingsSection.TEMPLATES -> s.copy(defaultWeeklySkeleton = g.defaultWeeklySkeleton)
            SettingsSection.SUNDAYS -> s.copy(sundayPolicy = g.sundayPolicy)
            SettingsSection.REQUESTS -> s.copy(requests = g.requests)
            SettingsSection.REST_RULES -> s.copy(restRulesForAllContracts = g.restRulesForAllContracts)
            SettingsSection.STAFF_CHECKS -> s.copy(blockExpiredStaffChecks = g.blockExpiredStaffChecks)
            SettingsSection.PAYROLL -> s.copy(
                market = g.market,
                payroll = g.payroll.copy(unpaidUserIds = s.payroll.unpaidUserIds, unpaidPeriods = s.payroll.unpaidPeriods)
            )
            SettingsSection.BREAKS -> s.copy(breakPolicy = g.breakPolicy)
            SettingsSection.CLOCK_REVIEW -> s.copy(clockReview = g.clockReview)
            SettingsSection.GEOFENCE -> s.copy(geofence = g.geofence)
            SettingsSection.ALERTS -> s.copy(noShowAlertMinutes = g.noShowAlertMinutes, checklistAlertMinutes = g.checklistAlertMinutes)
            SettingsSection.WORK_HOURS -> s.copy(workHours = g.workHours)
            SettingsSection.REPORTS -> s.copy(reports = g.reports)
            SettingsSection.ROSTER_VISIBILITY -> s.copy(rosterVisibility = g.rosterVisibility)
            SettingsSection.WEATHER -> s.copy(weatherEnabled = g.weatherEnabled)
            SettingsSection.SPECIAL_DAYS -> s.copy(specialDaysEnabled = g.specialDaysEnabled)
            SettingsSection.FOOTER -> s.copy(showEvoShiftFooter = g.showEvoShiftFooter)
            SettingsSection.INCOME, SettingsSection.SALES_TARGETS, SettingsSection.LABOUR_TARGETS -> s
        }
        val financials = when (section) {
            SettingsSection.INCOME -> from.financials?.let { gf ->
                (into.financials ?: WorkplaceFinancials()).copy(
                    commissionPercent = gf.commissionPercent,
                    fixedRevenueMonthly = gf.fixedRevenueMonthly,
                    includeFixedRevenueInDailyFinancialReport = gf.includeFixedRevenueInDailyFinancialReport
                )
            } ?: into.financials
            SettingsSection.SALES_TARGETS -> from.financials?.let { gf -> (into.financials ?: WorkplaceFinancials()).copy(salesTargets = gf.salesTargets) } ?: into.financials
            SettingsSection.LABOUR_TARGETS -> from.financials?.let { gf ->
                (into.financials ?: WorkplaceFinancials()).copy(
                    labourTargetPercent = gf.labourTargetPercent,
                    labourTargetConvertedFromSales = gf.labourTargetConvertedFromSales,
                    salesPerLabourHourGoal = gf.salesPerLabourHourGoal
                )
            } ?: into.financials
            else -> into.financials
        }
        return SettingsValues(settings, financials)
    }

    /** A workplace's effective values: each section the group's when it follows it, else its own. */
    fun effective(own: SettingsValues, group: SettingsValues, inherited: Set<SettingsSection>): SettingsValues =
        inherited.fold(own) { acc, section -> copy(section, group, acc) }

    /**
     * Smart sync when a workplace saves [section] as [saved]: equal to the group's values it
     * follows the group (the save drops back to inherited), otherwise it is the workplace's own.
     */
    fun sourceAfterSave(section: SettingsSection, saved: SettingsValues, group: SettingsValues?): SectionSource =
        if (group != null && same(section, saved, group)) SectionSource.GROUP else SectionSource.OWN
}

/** SH-269: the server refused a group settings change (functions/groupSettings.js `details.reason`). */
class GroupSettingsRefusedException(val reason: Reason) : Exception("Group settings refused: $reason") {
    enum class Reason {
        /** The plan ended: settings are read-only. */
        PLAN_ENDED,
        /** "Made with EVoShift" can be hidden on a paid plan only. */
        FOOTER_NEEDS_PAID_PLAN,
        /** Only owners and financial managers switch the finances' sections. */
        NOT_FINANCES,
        /** Not a manager of the workplace (or not the group's owner). */
        NOT_ALLOWED,
        /** The workplace is in no group. */
        NO_GROUP,
        /** A value the server does not take. */
        INVALID_VALUE;

        companion object {
            fun of(reason: String?): Reason? = when (reason) {
                "PLAN_ENDED" -> PLAN_ENDED
                "FOOTER_NEEDS_PAID_PLAN" -> FOOTER_NEEDS_PAID_PLAN
                "NOT_FINANCES" -> NOT_FINANCES
                "NOT_A_MANAGER", "NOT_THE_OWNER" -> NOT_ALLOWED
                "NO_GROUP", "NOT_A_MEMBER" -> NO_GROUP
                "INVALID_VALUE", "MALFORMED" -> INVALID_VALUE
                else -> null
            }
        }
    }
}
